/**
 * hooks/useAuth.ts
 * ─────────────────────────────────────────────────────────────
 * Session management hook.
 *
 * - Checks /api/auth/me on app mount to validate existing cookie
 * - Exposes login state and current username to the whole app
 * - Intercepts global 401 responses and triggers logout
 * - Provides logout function that clears the server-side cookie
 * ─────────────────────────────────────────────────────────────
 */

import { useState, useEffect, useCallback } from 'react';

export interface AuthState {
  isAuthenticated: boolean;
  isChecking:      boolean;   // true during the initial /me check
  username:        string | null;
  expiresAt:       string | null;
}

const INITIAL_STATE: AuthState = {
  isAuthenticated: false,
  isChecking:      true,
  username:        null,
  expiresAt:       null,
};

export function useAuth() {
  const [auth, setAuth] = useState<AuthState>(INITIAL_STATE);

  // Check existing session on mount
  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', {
        credentials: 'include',
      });

      if (res.ok) {
        const data = await res.json();
        setAuth({
          isAuthenticated: true,
          isChecking:      false,
          username:        data.data?.username ?? null,
          expiresAt:       data.data?.expiresAt ?? null,
        });
      } else {
        setAuth({ ...INITIAL_STATE, isChecking: false });
      }
    } catch {
      // Network error — treat as not authenticated
      setAuth({ ...INITIAL_STATE, isChecking: false });
    }
  }, []);

  const login = useCallback((username: string, expiresAt?: string) => {
    setAuth({
      isAuthenticated: true,
      isChecking:      false,
      username,
      expiresAt:       expiresAt ?? null,
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', {
        method:      'POST',
        credentials: 'include',
      });
    } catch { /* ignore network errors on logout */ }

    setAuth({ ...INITIAL_STATE, isChecking: false });
  }, []);

  return { auth, login, logout, checkSession };
}
